import { FC, useMemo, useEffect, useRef, useCallback } from "react";
import { Stack, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next"; // Import useTranslation

import Flag from "icons/Flag";
import Exit from "icons/Exit";

import { Button } from "components/common/Button";
import { ConfirmationModal } from "components/common/modals/ConfirmationModal";
import { ExperimentTastingQuestion } from "./components/ExperimentTastingQuestion";
import { Alert } from "components/common/Alert";
import { Input } from "components/common/inputs/Input";
import { ToastMessage } from "components/common/ToastMessage";

import {
  StyledExperimentQuestionsInstructionsContainer,
  StyledExperimentTasting,
  StyledExperimentTastingBtnsContainer,
  StyledExperimentTastingContainer,
  StyledExperimentTastingHeader,
  StyledExperimentTastingList,
  StyledExperimentTastingSubheader,
} from "styles/features/experiment";

export type QuestionType = {
  id: string;
  text: string;
};

interface ExperimentTastingProps {
  experimentName?: string;
  isExperimentLeaveModalOpen?: boolean;
  questions?: QuestionType[];
  instructions?: string | null;
  answers?: {
    [key: string]: "A" | "B" | "same";
  };
  isNeedHelp?: boolean;
  currentQuestionsSection?: number;
  questionsSectionCount?: number;
  questionsInSectionCount?: number;
  error?: string | null;

  handleToastClose: () => void;
  onHelpAsk?: () => void;
  onExperimentLeaveModalOpen?: () => void;
  onExperimentLeaveModalClose?: () => void;
  onExperimentLeave?: () => void;
  onAnswerChange?: (questionId: string) => (answer: "A" | "B" | "same") => void;
  onSubmit?: () => void;
  onNeedHelpCancel?: () => void;
  handleQuestionsSectionPrevChange?: () => void;
}

const ExperimentTasting: FC<ExperimentTastingProps> = ({
  experimentName,
  isExperimentLeaveModalOpen,
  questions,
  answers,
  isNeedHelp,
  instructions,
  currentQuestionsSection,
  questionsSectionCount,
  questionsInSectionCount,
  error,
  onHelpAsk,
  onExperimentLeave,
  onExperimentLeaveModalOpen,
  onExperimentLeaveModalClose,
  onAnswerChange,
  onSubmit,
  onNeedHelpCancel,
  handleQuestionsSectionPrevChange,
  handleToastClose,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(); // Initialize translation

  const isInitialRender = useRef(true);
  const headerRef = useRef<HTMLDivElement>(null);

  const scrollToTop = useCallback(() => {
    if (headerRef && headerRef.current) {
      headerRef.current.scrollIntoView({ block: "start", behavior: "smooth" });
    }
  }, []);

  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
    } else {
      scrollToTop();
    }
  }, [currentQuestionsSection, scrollToTop]);
  const isDisabledBtn = useMemo(() => {
    let includedAnswers: string[] = [];

    questions?.forEach((question) => {
      const includedAnswer = answers?.[question.id];

      if (includedAnswer) {
        includedAnswers = [...includedAnswers, includedAnswer];
      }
    });

    return !(includedAnswers.length === questionsInSectionCount);
  }, [answers, questions, questionsInSectionCount]);

  const renderedNeedHelpInfo = useMemo(
    () =>
      isNeedHelp ? (
        <Alert
          severity="info"
          text={t("help_on_way")}
          className="need-help-info"
          tools={
            <Button size="xs" onClick={onNeedHelpCancel} variant="text">
              {t("cancel")} {}
            </Button>
          }
        />
      ) : null,
    [isNeedHelp, onNeedHelpCancel, t]
  );

  const renderedBackBtn = useMemo(
    () =>
      currentQuestionsSection && currentQuestionsSection > 1 ? (
        <Button
          size="md"
          onClick={handleQuestionsSectionPrevChange}
          className="answers-submit"
          variant="secondary"
        >
          {t("back")} {}
        </Button>
      ) : null,
    [currentQuestionsSection, handleQuestionsSectionPrevChange, t]
  );

  const renderedInstructions = useMemo(
    () =>
      instructions && instructions.length > 0 ? (
        <StyledExperimentQuestionsInstructionsContainer>
          <Input
            multiline
            rows={6}
            disabled
            label={t("instructions")}
            value={instructions}
            withErrorField={false}
            className="instructions-field"
          />
        </StyledExperimentQuestionsInstructionsContainer>
      ) : null,
    [instructions, t]
  );

  return (
    <StyledExperimentTasting ref={headerRef}>
      <StyledExperimentTastingHeader>
        <Typography
          variant="h1"
          color={theme.palette.common.black}
          marginBottom="8px"
        >
          {experimentName}
        </Typography>
        <Button
          size="md"
          startIcon={<Exit />}
          variant="secondary"
          maxWidth="350px"
          onClick={onExperimentLeaveModalOpen}
        >
          {t("exit_experiment")} {}
        </Button>
      </StyledExperimentTastingHeader>
      <StyledExperimentTastingSubheader>
        {renderedNeedHelpInfo}
        <Button
          variant="grey"
          size="md"
          startIcon={<Flag />}
          className="experiment-need-help"
          maxWidth="290px"
          onClick={onHelpAsk}
          disabled={isNeedHelp}
        >
          {t("need_help")} {}
        </Button>
        <Stack
          className="experiment-products-headings"
          display="flex"
          flexDirection="column"
          gap="8px"
        >
          <Typography
            variant="h2"
            color={theme.palette.common.black}
            textAlign={theme.direction === "rtl" ? "end" : "start"}
          >
            {t("answer_questions")} {}
          </Typography>
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            textAlign={theme.direction === "rtl" ? "end" : "start"}
          >
            {t("answer_question_block")} {}
          </Typography>
        </Stack>
      </StyledExperimentTastingSubheader>
      <StyledExperimentTastingContainer>
        <Stack
          display="flex"
          flexDirection={theme.direction === "rtl" ? "row-reverse" : "row"}
          alignItems="flex-start"
          justifyContent="space-between"
          gap="16px"
        >
          <Typography
            variant="bodyL"
            color={theme.palette.common.black}
            fontWeight={500}
            className="questionnaire-text"
          >
            {t("before_tasting")} {}
          </Typography>
          <Typography
            variant="bodyM"
            color={theme.palette.common.black}
            fontWeight={400}
            className="questionnaire-steps"
          >
            {t("questions_section", {
              current: currentQuestionsSection,
              total: questionsSectionCount,
            })}{" "}
            {/* Use translation with variables */}
          </Typography>
        </Stack>
        {renderedInstructions}
        <StyledExperimentTastingList>
          {questions?.map((question, index) => (
            <ExperimentTastingQuestion
              key={question.id}
              index={index + 1}
              question={question}
              onOptionChange={onAnswerChange?.(question.id)}
              value={answers?.[question.id]}
            />
          ))}
        </StyledExperimentTastingList>
        <StyledExperimentTastingBtnsContainer>
          {renderedBackBtn}
          <Button
            size="md"
            disabled={isDisabledBtn}
            onClick={onSubmit}
            className="answers-submit"
          >
            {currentQuestionsSection === questionsSectionCount
              ? t("submit_answers")
              : t("next")}
          </Button>
        </StyledExperimentTastingBtnsContainer>
      </StyledExperimentTastingContainer>
      <ConfirmationModal
        open={!!isExperimentLeaveModalOpen}
        type="info"
        title={t("exit_experiment_title")}
        text={t("exit_experiment_text")}
        submitBtnText={t("confirm_exit")}
        cancelBtnText={t("stay_in_experiment")}
        onClose={onExperimentLeaveModalClose}
        onCancel={onExperimentLeaveModalClose}
        onSubmit={onExperimentLeave}
      />
      <ToastMessage
        severity="error"
        open={!!error}
        message={error as string}
        onClose={handleToastClose}
      />
    </StyledExperimentTasting>
  );
};

export default ExperimentTasting;
